import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  Avatar,
  IconButton,
  Chip,
  Card,
  CardContent,
  Divider,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Styled components
const WizardContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 800,
  margin: '0 auto',
  padding: theme.spacing(3)
}));

const UploadButton = styled('label')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover
  }
}));

const AvatarUpload = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(3)
}));

const LargeAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  marginBottom: theme.spacing(2),
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[3]
}));

const OptionCard = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  transform: selected ? 'translateY(-4px)' : 'none',
  border: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
  boxShadow: selected ? theme.shadows[4] : theme.shadows[1],
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-4px)'
  }
}));

const OnboardingWizard = ({ onComplete, loading, error }) => {
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    jobTitle: '',
    department: '',
    school: '',
    team: '',
    sports: [],
    bio: '',
    profileImage: null,
    preferences: {
      notifications: true,
      emailUpdates: true,
      dataSharing: false
    }
  });
  const [previewImage, setPreviewImage] = useState(null);
  
  // Available sports options
  const sportsOptions = [
    { value: 'football', label: 'Football' },
    { value: 'basketball', label: 'Basketball' },
    { value: 'baseball', label: 'Baseball' },
    { value: 'soccer', label: 'Soccer' },
    { value: 'volleyball', label: 'Volleyball' },
    { value: 'track', label: 'Track & Field' },
    { value: 'swimming', label: 'Swimming' },
    { value: 'tennis', label: 'Tennis' },
    { value: 'golf', label: 'Golf' },
    { value: 'lacrosse', label: 'Lacrosse' }
  ];
  
  // Handle profile data change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle preferences change
  const handlePreferenceChange = (e) => {
    const { name, checked } = e.target;
    setProfileData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [name]: checked
      }
    }));
  };
  
  // Handle sports selection
  const handleSportToggle = (sport) => {
    setProfileData(prev => {
      const sports = [...prev.sports];
      const index = sports.indexOf(sport);
      
      if (index === -1) {
        sports.push(sport);
      } else {
        sports.splice(index, 1);
      }
      
      return {
        ...prev,
        sports
      };
    });
  };
  
  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileData(prev => ({
        ...prev,
        profileImage: file
      }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle next step
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  // Handle back step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  // Handle complete
  const handleComplete = () => {
    onComplete(profileData);
  };
  
  // Onboarding steps
  const steps = [
    {
      label: 'Personal Information',
      description: 'Tell us about yourself',
      content: (
        <Box>
          <AvatarUpload>
            <LargeAvatar src={previewImage}>
              {!previewImage && <PersonIcon sx={{ fontSize: 60 }} />}
            </LargeAvatar>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="profile-image-upload"
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="profile-image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<AddPhotoAlternateIcon />}
              >
                Upload Photo
              </Button>
            </label>
          </AvatarUpload>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="firstName"
                value={profileData.firstName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Last Name"
                name="lastName"
                value={profileData.lastName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Title"
                name="jobTitle"
                value={profileData.jobTitle}
                onChange={handleChange}
                placeholder="e.g. Head Coach, Assistant Coach, Recruiter"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio"
                name="bio"
                value={profileData.bio}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Tell us a bit about yourself..."
              />
            </Grid>
          </Grid>
        </Box>
      )
    },
    {
      label: 'Organization Details',
      description: 'Tell us about your school or organization',
      content: (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="School/Organization"
                name="school"
                value={profileData.school}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Department"
                name="department"
                value={profileData.department}
                onChange={handleChange}
                placeholder="e.g. Athletics, Sports Management"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Team"
                name="team"
                value={profileData.team}
                onChange={handleChange}
                placeholder="e.g. Varsity Football"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Sports
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {profileData.sports.map(sport => (
                  <Chip
                    key={sport}
                    label={sportsOptions.find(option => option.value === sport)?.label || sport}
                    onDelete={() => handleSportToggle(sport)}
                    color="primary"
                  />
                ))}
              </Box>
              <Grid container spacing={1}>
                {sportsOptions.map(option => (
                  <Grid item key={option.value}>
                    <Chip
                      label={option.label}
                      onClick={() => handleSportToggle(option.value)}
                      variant={profileData.sports.includes(option.value) ? "filled" : "outlined"}
                      color={profileData.sports.includes(option.value) ? "primary" : "default"}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )
    },
    {
      label: 'Preferences',
      description: 'Set up your preferences',
      content: (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Notification Settings
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={profileData.preferences.notifications}
                  onChange={handlePreferenceChange}
                  name="notifications"
                />
              }
              label="Enable in-app notifications"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1 }}>
              Receive notifications about prospect updates, messages, and team activities
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={profileData.preferences.emailUpdates}
                  onChange={handlePreferenceChange}
                  name="emailUpdates"
                />
              }
              label="Receive email updates"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1 }}>
              Get important updates and summaries via email
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={profileData.preferences.dataSharing}
                  onChange={handlePreferenceChange}
                  name="dataSharing"
                />
              }
              label="Data sharing"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1 }}>
              Allow anonymous data to be used for improving the platform
            </Typography>
          </Paper>
        </Box>
      )
    },
    {
      label: 'Review',
      description: 'Review your information',
      content: (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Personal Information
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Avatar 
                  src={previewImage} 
                  sx={{ width: 80, height: 80, mb: 1 }}
                >
                  {!previewImage && <PersonIcon sx={{ fontSize: 40 }} />}
                </Avatar>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography variant="subtitle2">
                  {profileData.firstName} {profileData.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profileData.jobTitle}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {profileData.bio || 'No bio provided'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          
          <Typography variant="subtitle1" gutterBottom>
            Organization
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2">
              {profileData.school}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {profileData.department} {profileData.team ? `• ${profileData.team}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              {profileData.sports.map(sport => (
                <Chip
                  key={sport}
                  label={sportsOptions.find(option => option.value === sport)?.label || sport}
                  size="small"
                />
              ))}
            </Box>
          </Paper>
          
          <Typography variant="subtitle1" gutterBottom>
            Preferences
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon 
                    color={profileData.preferences.notifications ? "success" : "disabled"} 
                    sx={{ mr: 1 }} 
                  />
                  <Typography variant="body2">
                    In-app notifications: {profileData.preferences.notifications ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon 
                    color={profileData.preferences.emailUpdates ? "success" : "disabled"} 
                    sx={{ mr: 1 }} 
                  />
                  <Typography variant="body2">
                    Email updates: {profileData.preferences.emailUpdates ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon 
                    color={profileData.preferences.dataSharing ? "success" : "disabled"} 
                    sx={{ mr: 1 }} 
                  />
                  <Typography variant="body2">
                    Data sharing: {profileData.preferences.dataSharing ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )
    }
  ];
  
  return (
    <WizardContainer>
      <Typography variant="h4" gutterBottom align="center">
        Welcome to TeamScout
      </Typography>
      <Typography variant="body1" paragraph align="center" color="text.secondary">
        Let's set up your profile to get the most out of the platform
      </Typography>
      
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>
              <Typography variant="subtitle1">{step.label}</Typography>
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                {step.description}
              </Typography>
              {step.content}
              <Box sx={{ mb: 2, mt: 3 }}>
                <div>
                  <Button
                    variant="contained"
                    onClick={index === steps.length - 1 ? handleComplete : handleNext}
                    sx={{ mt: 1, mr: 1 }}
                    endIcon={index === steps.length - 1 ? <CheckCircleIcon /> : <ArrowForwardIcon />}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : index === steps.length - 1 ? (
                      'Complete Setup'
                    ) : (
                      'Continue'
                    )}
                  </Button>
                  <Button
                    disabled={index === 0 || loading}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </Button>
                </div>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
      
      {activeStep === steps.length && (
        <Paper square elevation={0} sx={{ p: 3, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            All set!
          </Typography>
          <Typography paragraph>
            Your profile is complete and you're ready to start using TeamScout.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            href="/dashboard"
            sx={{ mt: 1 }}
          >
            Go to Dashboard
          </Button>
        </Paper>
      )}
    </WizardContainer>
  );
};

export default OnboardingWizard;
